import { Component, HostBinding, Input } from '@angular/core';
import { ButtonTypeEnum } from './types/button-type.enum';
import { StringHelper } from '../../../helpers/string.helper';
import { ButtonSizeEnum } from './types/button-size.enum';
import { ButtonRadiusEnum } from './types/button-radius.enum';

@Component({
    selector: '[lButton]',
    standalone: true,
    template: `<ng-content></ng-content>`,
})
export class ButtonComponent {

  @HostBinding('class')
  public get class(): string {
    return this.generateClasses;
  }

  @Input()
  public set bType(content: ButtonTypeEnum | string) {
    this._type = StringHelper.stringToEnum(ButtonTypeEnum, content) ?? ButtonTypeEnum.BASIC;
  };

  public set type(content: ButtonTypeEnum | string) {
    this._type = StringHelper.stringToEnum(ButtonTypeEnum, content) ?? ButtonTypeEnum.BASIC;
  }

  @Input()
  public set bSize(content: ButtonSizeEnum | string) {
    this._size = StringHelper.stringToEnum(ButtonSizeEnum, content) ?? ButtonSizeEnum.MEDIUM;
  };

  public set size(content: ButtonSizeEnum | string) {
    this._size = StringHelper.stringToEnum(ButtonSizeEnum, content) ?? ButtonSizeEnum.MEDIUM;
  }

  @Input()
  public set bRadius(content: ButtonRadiusEnum | string) {
    this._radius = StringHelper.stringToEnum(ButtonRadiusEnum, content) ?? ButtonRadiusEnum.BASE;
  };

  public set radius(content: ButtonRadiusEnum | string) {
    this._radius = StringHelper.stringToEnum(ButtonRadiusEnum, content) ?? ButtonRadiusEnum.BASE;
  }

  @Input()
  public set outline(content: boolean | string) {
    if (typeof content === 'string' && content === '') {
      this._outline = true;
      return;
    }

    this._outline = !!content;
  };

  private _type: ButtonTypeEnum = ButtonTypeEnum.BASIC;

  private _size: ButtonSizeEnum = ButtonSizeEnum.FULL;

  private _outline: boolean = false;

  private _radius: ButtonRadiusEnum = ButtonRadiusEnum.BASE;

  private get generateClasses(): string {
    const colors: Record<ButtonTypeEnum, string> = {
      [ButtonTypeEnum.BASIC]: 'bg-lWhite text-lBlack shadow-neon-white hover:shadow-neon-primary hover:text-white hover:bg-lPrimary focus:ring-lGray800/50 border-0',
      [ButtonTypeEnum.OUTLINE]: 'border border-lSecondary bg-transparent text-lSecondary !cursor-default',
      [ButtonTypeEnum.SUCCESS]: 'bg-lSuccess hover:bg-lSuccess/90 focus:ring-lSuccess/50 border-0 text-white',
      [ButtonTypeEnum.WARNING]: 'bg-lWarning hover:bg-lWarning/90 focus:ring-lWarning/50 border-0 text-white',
      [ButtonTypeEnum.DANGER]: 'bg-lDanger hover:bg-lDanger/90 focus:ring-lDanger/50 border-0 text-white',
      [ButtonTypeEnum.PRIMARY]: 'bg-lPrimary shadow-neon-primary hover:bg-lPrimaryLight focus:ring-lPrimary/50 border-0 text-white transition-all duration-300',
      [ButtonTypeEnum.DISABLED]: 'bg-lGray700 text-lGray500 shadow-none border-0 select-none',
    }

    const colorsOutline: Record<ButtonTypeEnum, string> = {
      [ButtonTypeEnum.BASIC]: 'border border-lSecondary bg-transparent text-lSecondary hover:bg-lSecondary/10 hover:border-lSecondary/30 focus:ring-lSecondary/20 transition-all duration-300',
      [ButtonTypeEnum.OUTLINE]: 'border border-lWhite/20 bg-transparent hover:bg-lWhite/5 hover:border-lWhite/30 focus:ring-lWhite/20 text-lWhite transition-all duration-300',
      [ButtonTypeEnum.SUCCESS]: 'border border-lSuccess bg-lSuccess/10 hover:bg-lSuccess/15 focus:ring-lSuccess/50 text-lSuccess',
      [ButtonTypeEnum.WARNING]: 'border border-lWarning bg-lWarning/10 hover:bg-lWarning/15 focus:ring-lWarning/50 text-lWarning',
      [ButtonTypeEnum.DANGER]: 'border border-lDanger bg-lDanger/10 hover:bg-lDanger/15 focus:ring-lDanger/50 text-lDanger',
      [ButtonTypeEnum.PRIMARY]: 'border border-lPrimary bg-transparent hover:bg-lPrimary/10 hover:shadow-lg hover:shadow-lPrimary/30 focus:ring-lPrimary/50 text-lPrimary transition-all duration-300',
      [ButtonTypeEnum.DISABLED]: 'border border-lGray700 bg-transparent text-lGray500 select-none',
    }

    const radius: Record<ButtonRadiusEnum, string> = {
      [ButtonRadiusEnum.SMALL]: 'rounded-sm',
      [ButtonRadiusEnum.BASE]: 'rounded-xl',
      [ButtonRadiusEnum.LARGE]: 'rounded-3xl',
      [ButtonRadiusEnum.CIRCLE]: 'rounded-full',
    }

    const sizes: Record<ButtonSizeEnum, string> = {
      [ButtonSizeEnum.SMALL]: 'h-8 p-2 w-fit',
      [ButtonSizeEnum.MEDIUM]: 'h-12 p-3 min-w-60',
      [ButtonSizeEnum.LARGE]: 'h-16 p-4 min-w-80',
      [ButtonSizeEnum.FULL]: 'h-12 p-3 w-full',
    }

    const baseClasses = 'no-underline inline-flex items-center justify-center text-center focus:outline-none focus:ring !cursor-pointer disabled:cursor-default disabled:pointer-events-none transition-colors duration-300';
    return (this._outline ? colorsOutline[this._type] : colors[this._type]) + ' ' + sizes[this._size] + ' ' + radius[this._radius] + ' ' + baseClasses;
  }

}
